

import * as React from "react";
import "../styles/tableSection.css";
import { AdContext } from "../context/AdContext";
import { midpointApiService } from "../services/midpointApi";
import { MidpointAuthenticationError } from "../services/midpointAuth";
import Loader from "../components/Loader";
import Pagination from "../components/Pagination";
import AddMemberModal from "../components/AddMemberModal";
import ConfirmationModal from "../components/ConfirmationModal";

const api_url_endpoint = process.env.REACT_APP_API_URL;
const content_type = process.env.REACT_APP_CONTENT_TYPE;
const useMidpoint = process.env.REACT_APP_USE_MIDPOINT === 'true';

interface Member {
  displayName: string;
  userPrincipalName: string;
  jobTitle: string;
  id: string;
}

// Type guard for Error objects
function isError(error: unknown): error is Error {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as any).message === 'string'
  );
}

const TableSection: React.FC = () => {
  const {
    selectedRows,
    setSelectedRows,
    deleteModal,
    setDeleteModal,
    addModal,
    setAddModal,
    addToast,
    setAddToast,
    removeToast,
    setRemoveToast,
    group,
    setGroup,
    setResources,
    refreshTableFlag,
    setRefreshTableFlag,
    navigateToDashboard,
  } = React.useContext(AdContext);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [currentPage_Main, setCurrentPage_Main] = React.useState<number>(1);
  const [members, setMembers] = React.useState<Member[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isRemoving, setIsRemoving] = React.useState(false);
  const [removingMemberId, setRemovingMemberId] = React.useState<string | null>(null);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = React.useState(false);
  const [memberToRemove, setMemberToRemove] = React.useState<Member | null>(null);
  const [isRefreshingAfterRemoval, setIsRefreshingAfterRemoval] = React.useState(false);

  React.useEffect(() => {
    if (members) {
      setResources(members.length);
    }
  }, [members]);

  // Handle URL-based group loading (for direct links and page reloads)
  React.useEffect(() => {
    const handleUrlGroup = async () => {
      // Group already restored from sessionStorage in AdContext — no API call needed
      if (group) return;

      // No sessionStorage data and no group in context — redirect to dashboard
      navigateToDashboard();
    };

    handleUrlGroup();
  }, []); // Empty dependency array - only run once on mount

  const [pagination, setPagination] = React.useState<Record<string, any>>({
    totalResults: 0,
    totalPages: 0,
    page: 1,
    limit: 10,
  });
  const [limitNum, setLimitNum] = React.useState<number>(10);

  const [sortConfig, setSortConfig] = React.useState<{
    key: keyof Member | null;
    direction: "asc" | "desc" | null;
  }>({
    key: null,
    direction: null,
  });

  const handleMemberAdded = () => {
    // Trigger refresh of the members list
    setRefreshTableFlag(prev => !prev);
  };

  const handleRemoveClick = (member: Member) => {
    setMemberToRemove(member);
    setShowRemoveConfirmation(true);
  };

  const handleRemoveConfirm = async () => {
    if (!memberToRemove || !group?.id) return;

    try {
      setIsRemoving(true);
      setRemovingMemberId(memberToRemove.id);
      setShowRemoveConfirmation(false);

      console.log(`Starting removal process for member: ${memberToRemove.displayName}`);

      if (useMidpoint) {
        // Use Midpoint API
        await midpointApiService.removeMemberFromGroup(memberToRemove.id, group.id);
      } else {
        // Use legacy API
        const response = await fetch(
          `${api_url_endpoint}/ad-management/ad-handler/remove-group-member`,
          {
            method: "POST",
            headers: {
              "Content-Type": content_type || "application/json",
            },
            body: JSON.stringify({
              groupId: group.id,
              userId: memberToRemove.id,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.message || 'Failed to remove member');
        }
      }

      console.log('Member removal successful, refreshing member list...');

      // Show refresh loading state
      setIsRefreshingAfterRemoval(true);

      // Give Midpoint time to process the removal, then refresh
      setTimeout(() => {
        console.log('Triggering member list refresh');
        setRefreshTableFlag(prev => !prev);
      }, 2000);

    } catch (error: unknown) {
      let errorMessage = 'Failed to remove member';

      if (error instanceof MidpointAuthenticationError) {
        errorMessage = `Authentication Error: ${error.message}`;
      } else if (isError(error)) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      // Show error message
      if (setRemoveToast) {
        setRemoveToast(true);
      }

      console.error("Remove member error:", error);

      // Reset states on error
      setIsRemoving(false);
      setRemovingMemberId(null);
      setIsRefreshingAfterRemoval(false);
      setMemberToRemove(null);
    }
  };

  const handleRemoveCancel = () => {
    setShowRemoveConfirmation(false);
    setMemberToRemove(null);
  };

  React.useEffect(() => {
    const fetchMembers = async () => {
      if (!group?.id) {
        setMembers([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        if (useMidpoint) {
          // Use midpoint API
          const membersData = await midpointApiService.getGroupMembers(group.id);
          setMembers(membersData);
        } else {
          // Use legacy API
          const response = await fetch(
            `${api_url_endpoint}/ad-management/ad-handler/get-group-members-by-id`,
            {
              method: "POST",
              headers: {
                "Content-Type": content_type || "application/json",
              },
              body: JSON.stringify({
                groupId: group.id,
              }),
            }
          );
          const result = await response.json();

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          if (result.success && Array.isArray(result.data)) {
            setMembers(result.data);
          } else {
            setMembers([]);
          }
        }

        // Show success message if this was after a removal
        if (isRefreshingAfterRemoval && setAddToast) {
          setAddToast(true);
        }

        console.log('Members list refreshed successfully');

      } catch (error: unknown) {
        let errorMessage = 'Failed to load group members';

        if (error instanceof MidpointAuthenticationError) {
          errorMessage = `Authentication Error: ${error.message}. Please check your credentials.`;
        } else if (isError(error)) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        } else {
          errorMessage = 'An unexpected error occurred';
        }

        setError(errorMessage);
        setMembers([]);
        console.error("Fetch group members error:", error);
      } finally {
        setLoading(false);

        // Reset removal-related loading states
        setIsRemoving(false);
        setRemovingMemberId(null);
        setIsRefreshingAfterRemoval(false);
        setMemberToRemove(null);
      }
    };

    fetchMembers();
  }, [group?.id, refreshTableFlag]);

  const filteredMembers = members.filter((m) => {
    const s = searchTerm?.toLowerCase();
    return (
      m.displayName?.toLowerCase().includes(s) ||
      m.userPrincipalName?.toLowerCase().includes(s) ||
      m.jobTitle?.toLowerCase().includes(s)
    );
  });

  const handleSort = (key: keyof Member) => {
    setSortConfig((prev) => {
      if (prev.key !== key) {
        return { key, direction: "asc" };
      }
      if (prev.direction === "asc") {
        return { key, direction: "desc" };
      }
      return { key: null, direction: null };
    });
  };
  const sortedMembers = React.useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) {
      return filteredMembers;
    }
    const sortKey: keyof Member = sortConfig.key;
    return [...filteredMembers].sort((a, b) => {
      const aVal = a[sortKey] ?? "";
      const bVal = b[sortKey] ?? "";
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      return sortConfig.direction === "asc"
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  }, [filteredMembers, sortConfig]);

  React.useEffect(() => {
    const total = filteredMembers.length;
    const totalPages = Math.ceil(total / limitNum);
    setPagination((prev: any) => ({
      ...prev,
      totalResults: total,
      totalPages: totalPages,
      page: currentPage_Main,
      limit: limitNum,
    }));
  }, [loading]);

  const startIndex = (currentPage_Main - 1) * limitNum;
  const endIndex = startIndex + limitNum;
  const paginatedData = sortedMembers.slice(startIndex, endIndex);

  const MAX_SELECT = 20;
  const handleRowSelect = (row: Member) => {
    setSelectedRows((prev) => {
      const exists = prev.some(
        (r) => r.userPrincipalName === row.userPrincipalName
      );
      if (exists) {
        return prev.filter(
          (r) => r.userPrincipalName !== row.userPrincipalName
        );
      }
      if (prev.length >= MAX_SELECT) return prev;
      return [...prev, row];
    });
  };
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (checked) {
      const currentSelectedCount = selectedRows.length;
      const rowsToAdd = paginatedData.filter(
        (row) =>
          !selectedRows.some(
            (r) => r.userPrincipalName === row.userPrincipalName
          )
      );
      const availableSlots = MAX_SELECT - currentSelectedCount;
      const limitedRowsToAdd = rowsToAdd.slice(0, availableSlots);
      setSelectedRows([...selectedRows, ...limitedRowsToAdd]);
    } else {
      const remaining = selectedRows.filter(
        (row) =>
          !paginatedData.some(
            (r) => r.userPrincipalName === row.userPrincipalName
          )
      );
      setSelectedRows(remaining);
    }
  };
  const isAllSelected = paginatedData.every((row) =>
    selectedRows.some((r) => r.userPrincipalName === row.userPrincipalName)
  );
  const isRowSelected = (email: string) =>
    selectedRows.some((r) => r.userPrincipalName === email);

  React.useEffect(() => {
    setCurrentPage_Main(1);
  }, [searchTerm]);

  return (
    <>
      <div className="members-container">
        <div className="members-header">
          <h3>
            Members
            <span className="count">
              {String(members.length).padStart(2, "0")}
            </span>
          </h3>
          {selectedRows.length <= 0 && (
            <div style={{ display: "flex", gap: "12px" }}>
              <div
                style={{
                  width: "260px",
                  background: "var(--bg-input)",
                  border: "1px solid var(--border)",
                  borderRadius: "6px",
                  padding: "6px 10px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z"
                    stroke="#4A5B70"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Search User"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    border: "none",
                    outline: "none",
                    width: "100%",
                    fontSize: "13px",
                    color: "var(--text-primary)",
                    background: "transparent",
                  }}
                />
              </div>
              <button className="add-btn" onClick={() => setAddModal && setAddModal(true)}>
                + Add New Member
              </button>
            </div>
          )}
          {selectedRows.length > 0 && (
            <div
              style={{ display: "flex", gap: "12px" }}
              onClick={() => setDeleteModal && setDeleteModal(true)}
            >
              <button className="del-btn" style={{ cursor: "pointer" }}>
                Remove Members
              </button>
            </div>
          )}
        </div>
        {error && (
          <div style={{
            backgroundColor: "var(--red-dim)",
            border: "1px solid rgba(240,68,56,0.3)",
            borderRadius: "6px",
            padding: "12px",
            margin: "12px 0",
            color: "#F87171",
            fontSize: "13px"
          }}>
            <strong>Error loading members:</strong> {error}
          </div>
        )}
        {isRefreshingAfterRemoval && (
          <div style={{
            backgroundColor: "var(--bg-elevated)",
            border: "1px solid var(--border)",
            borderRadius: "6px",
            padding: "14px 16px",
            margin: "12px 0",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            color: "var(--text-secondary)",
            fontSize: "13px"
          }}>
            <div
              style={{
                width: "15px",
                height: "15px",
                border: "2px solid var(--border)",
                borderTop: "2px solid var(--accent)",
                borderRadius: "50%",
                animation: "spin 1s linear infinite"
              }}
            />
            Refreshing member list...
          </div>
        )}
        {loading && <Loader />}
        {!loading && (
          <table className="members-table">
            <thead>
              <tr>
                <th>
                  <input
                    style={{ cursor: "pointer" }}
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    disabled={paginatedData.length === 0 || (selectedRows.length >= MAX_SELECT && !isAllSelected)}
                  />
                </th>
                <th onClick={() => handleSort("displayName")}>Member</th>
                <th onClick={() => handleSort("userPrincipalName")}>Email</th>
                <th onClick={() => handleSort("jobTitle")}>Job Title</th>
                <th style={{ textAlign: "center" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length > 0 ? (
                paginatedData.map((m, index) => (
                  <tr key={index} className="my-tr-section">
                    <td>
                      <input
                        type="checkbox"
                        checked={isRowSelected(m.userPrincipalName)}
                        onChange={() => handleRowSelect(m)}
                        disabled={
                          !isRowSelected(m.userPrincipalName) && selectedRows.length >= MAX_SELECT
                        }
                      />
                    </td>
                    <td>{m.displayName}</td>
                    <td>{m.userPrincipalName}</td>
                    <td>{m.jobTitle}</td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        onClick={() => handleRemoveClick(m)}
                        disabled={isRemoving || removingMemberId === m.id}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: (isRemoving || removingMemberId === m.id) ? 'not-allowed' : 'pointer',
                          padding: '6px',
                          borderRadius: '4px',
                          color: '#EF4444',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: (isRemoving || removingMemberId === m.id) ? 0.5 : 1,
                          transition: 'all 0.2s',
                          minWidth: '28px',
                          minHeight: '28px'
                        }}
                        onMouseEnter={(e) => {
                          if (!isRemoving && removingMemberId !== m.id) {
                            e.currentTarget.style.backgroundColor = '#FEF2F2';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        title={`Remove ${m.displayName} from group`}
                      >
                        {removingMemberId === m.id ? (
                          // Show spinner for the specific member being removed
                          <div
                            style={{
                              width: '14px',
                              height: '14px',
                              border: '2px solid #EF4444',
                              borderTop: '2px solid transparent',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite'
                            }}
                          />
                        ) : (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M3 6h18"/>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                            <line x1="10" y1="11" x2="10" y2="17"/>
                            <line x1="14" y1="11" x2="14" y2="17"/>
                          </svg>
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center" }}>
                    No members found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      <div style={{ marginTop: 16 }}>
        <Pagination
          setcurrentPage={setCurrentPage_Main}
          pagination={pagination}
          setPagination={setPagination}
          limitNum={limitNum}
          setLimitNum={setLimitNum}
        />
      </div>

      {addModal && group && (
        <AddMemberModal
          isOpen={addModal}
          onClose={() => setAddModal && setAddModal(false)}
          onMemberAdded={handleMemberAdded}
          groupId={group.id}
          groupName={group.displayName}
          existingMembers={members}
        />
      )}

      {/* Confirmation modal for removing members */}
      <ConfirmationModal
        isOpen={showRemoveConfirmation}
        onClose={handleRemoveCancel}
        onConfirm={handleRemoveConfirm}
        type="danger"
        title="Remove Member"
        message={
          memberToRemove
            ? `Are you sure you want to remove "${memberToRemove.displayName}" from the group "${group?.displayName}"? This action cannot be undone.`
            : ""
        }
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={isRemoving}
      />
    </>
  );
};

export default TableSection;
